import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { markToolCompleted } from "@/lib/journeyTracker";
import { saveJourneyRef } from "@/lib/journeyRef";
import { toast } from "sonner";

const CHANNELS = [
  { value: "chiamata", label: "Telefonata" },
  { value: "whatsapp", label: "WhatsApp" },
];
const SLOTS = [
  { value: "mattina", label: "Mattina" },
  { value: "pomeriggio", label: "Pomeriggio" },
  { value: "sera", label: "Sera" },
];

export default function OrizzontiCallPage() {
  const [params] = useSearchParams();
  const j = params.get("j") || "";
  const t = params.get("t") || "";

  const [phase, setPhase] = useState("checking"); // checking | invalid | form | already | success
  const [errorMsg, setErrorMsg] = useState("");
  const [paName, setPaName] = useState(null);

  const [telefono, setTelefono] = useState("");
  const [canale, setCanale] = useState("chiamata");
  const [fascia, setFascia] = useState("mattina");
  const [consenso, setConsenso] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!j || !t) {
        setPhase("invalid");
        setErrorMsg("Il collegamento non è completo. Riparti dal messaggio ricevuto via email.");
        return;
      }
      try {
        const r = await api.get(`/orizzonti/verify`, { params: { j, t } });
        if (cancelled) return;
        if (r.data?.agent_display_name) setPaName(r.data.agent_display_name);
        // Il collegamento tokenizzato è la prova che OrizzontiPro è stato
        // completato: idrato il journey locale così che /app possa mostrare
        // avanzamento 1/3 e "Prossimo passo: ValutaPro" anche se l'utente
        // apre l'email in un browser diverso da quello del funnel.
        markToolCompleted("orizzonti", { journey_id: j });
        // Persist token alongside journey_id so downstream tools (ValutaPro,
        // PartenzaPro) can hydrate identity via /orizzonti/verify without
        // asking again. The token is user-scoped: it was already delivered
        // in the email, storing it in the same browser adds no PII risk.
        try {
          saveJourneyRef({ j, t });
        } catch { /* quota */ }
        if (r.data?.has_call_requested) {
          setPhase("already");
        } else {
          setPhase("form");
        }
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        const detail = e?.response?.data?.detail;
        setErrorMsg(
          detail ||
            (code === 401
              ? "Collegamento non valido. Se hai ricevuto l'email di recente, riprova dal pulsante nell'email."
              : code === 404
              ? "Il collegamento non è più disponibile. Riparti dal tuo percorso."
              : "Impossibile verificare il collegamento in questo momento. Riprova tra qualche minuto.")
        );
        setPhase("invalid");
      }
    }
    verify();
    return () => { cancelled = true; };
  }, [j, t]);

  const submit = async () => {
    if (submitting) return;
    setFormError("");
    const tel = telefono.trim();
    if (tel.length < 6 || tel.length > 30 || !/^[\d\s+()./-]+$/.test(tel)) {
      setFormError("Inserisci un numero di telefono valido.");
      return;
    }
    if (!consenso) {
      setFormError("Il consenso è necessario per inviare la richiesta.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/orizzonti/call-request", {
        journey_id: j,
        t,
        telefono: tel,
        canale_preferito: canale,
        fascia_oraria: fascia,
        consenso_contatto: true,
      });
      if (r.data?.already) {
        setPhase("already");
      } else {
        setPhase("success");
      }
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 401) {
        setPhase("invalid");
        setErrorMsg(detail || "Collegamento non valido.");
      } else if (code === 404) {
        setPhase("invalid");
        setErrorMsg(detail || "Collegamento scaduto o non più disponibile.");
      } else if (code === 429) {
        setFormError("Hai eseguito troppi tentativi in poco tempo. Riprova tra qualche minuto.");
      } else if (code === 400) {
        setFormError(detail || "Alcuni dati non sono validi. Verifica e riprova.");
      } else {
        setFormError("Errore temporaneo. Riprova tra qualche istante.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const backHref = "/app";

  return (
    <main data-testid="orizzonti-call-page" className="min-h-screen bg-gradient-to-b from-[#F5F5F5] to-white text-[#0A0A0A]">
      <header className="max-w-3xl mx-auto px-5 sm:px-8 py-6 flex items-center">
        <Logo size={28} />
      </header>

      <div className="max-w-2xl mx-auto px-5 sm:px-8 pb-16">
        {phase === "checking" && (
          <div
            className="bg-white rounded-2xl p-8 shadow-sm text-center"
            data-testid="orizzonti-call-checking"
          >
            <Loader2 className="animate-spin mx-auto text-[#B40101]" size={28} />
            <p className="mt-4 text-[#666]">Verifica del collegamento in corso…</p>
          </div>
        )}

        {phase === "invalid" && (
          <div
            className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm"
            data-testid="orizzonti-call-invalid"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-[#B40101] shrink-0 mt-1" size={22} />
              <div>
                <h1 className="font-display text-2xl font-bold">Collegamento non valido</h1>
                <p className="mt-3 text-[#666] leading-relaxed">{errorMsg}</p>
              </div>
            </div>
            <Link
              to={backHref}
              data-testid="orizzonti-call-invalid-back"
              className="mt-8 inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8E0101] text-white font-semibold px-5 py-3 rounded-lg transition-colors"
            >
              Torna al mio percorso <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {phase === "already" && (
          <div
            className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm"
            data-testid="orizzonti-call-already"
          >
            <div className="flex items-center gap-2 text-[#B40101] font-bold uppercase tracking-widest text-xs">
              <CheckCircle2 size={18} /> Richiesta già registrata
            </div>
            <h1 className="mt-3 font-display text-2xl sm:text-3xl font-bold">
              Il tuo Property Advisor è già stato avvisato.
            </h1>
            <p className="mt-4 text-[#666] leading-relaxed">
              Abbiamo trovato una richiesta di contatto precedente su questo percorso. {paName ? `${paName} ` : "Il tuo Property Advisor "}
              ti contatterà secondo la modalità e la fascia oraria che hai indicato.
            </p>
            <Link
              to={backHref}
              data-testid="orizzonti-call-already-back"
              className="mt-8 inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8E0101] text-white font-semibold px-5 py-3 rounded-lg transition-colors"
            >
              Torna al mio percorso <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {phase === "form" && (
          <div className="bg-white rounded-2xl p-6 sm:p-10 shadow-sm" data-testid="orizzonti-call-form">
            <div className="flex items-center gap-2 text-[#B40101] font-bold uppercase tracking-widest text-xs">
              <Phone size={16} /> Richiesta di contatto
            </div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold leading-tight">
              Parla con il tuo Property Advisor
            </h1>
            <p className="mt-4 text-[#666] leading-relaxed">
              {paName ? `${paName} ` : "Il tuo Property Advisor "}
              partirà dalle analisi che hai già completato. Indicaci come preferisci essere contattato e in quale momento.
            </p>

            <form
              className="mt-8 space-y-6"
              onSubmit={(e) => { e.preventDefault(); submit(); }}
            >
              <div>
                <label className="block text-sm font-semibold text-[#0A0A0A] mb-2" htmlFor="oz-call-tel">
                  Numero di telefono *
                </label>
                <input
                  id="oz-call-tel"
                  data-testid="orizzonti-call-tel"
                  type="tel"
                  autoComplete="tel"
                  className="w-full border border-[#E5E5E5] rounded-lg px-4 py-3 focus:outline-none focus:border-[#B40101] focus:ring-2 focus:ring-[#B40101]/10"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="+39 ___ ___ ____"
                  required
                />
              </div>

              <div>
                <span className="block text-sm font-semibold text-[#0A0A0A] mb-2">Modalità preferita *</span>
                <div className="grid grid-cols-2 gap-3">
                  {CHANNELS.map((c) => (
                    <button
                      type="button"
                      key={c.value}
                      data-testid={`orizzonti-call-canale-${c.value}`}
                      onClick={() => setCanale(c.value)}
                      className={`px-4 py-3 rounded-lg border text-sm font-semibold transition-colors ${
                        canale === c.value
                          ? "border-[#B40101] bg-[#FFF5F5] text-[#B40101]"
                          : "border-[#E5E5E5] text-[#0A0A0A] hover:border-[#B40101]/40"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="block text-sm font-semibold text-[#0A0A0A] mb-2">Fascia oraria *</span>
                <div className="grid grid-cols-3 gap-3">
                  {SLOTS.map((s) => (
                    <button
                      type="button"
                      key={s.value}
                      data-testid={`orizzonti-call-fascia-${s.value}`}
                      onClick={() => setFascia(s.value)}
                      className={`px-3 py-3 rounded-lg border text-sm font-semibold transition-colors ${
                        fascia === s.value
                          ? "border-[#B40101] bg-[#FFF5F5] text-[#B40101]"
                          : "border-[#E5E5E5] text-[#0A0A0A] hover:border-[#B40101]/40"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="orizzonti-call-consenso"
                  className="mt-1 h-4 w-4 accent-[#B40101]"
                  checked={consenso}
                  onChange={(e) => setConsenso(e.target.checked)}
                />
                <span className="text-sm text-[#0A0A0A] leading-relaxed">
                  Chiedo di essere contattato dal Property Advisor assegnato per ricevere gratuitamente un orientamento sulle mie analisi. La richiesta non comporta incarichi o impegni commerciali.
                </span>
              </label>

              {formError && (
                <p className="text-sm text-[#B40101]" data-testid="orizzonti-call-form-error">{formError}</p>
              )}

              <button
                type="submit"
                data-testid="orizzonti-call-submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#B40101] hover:bg-[#8E0101] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-5 py-4 rounded-lg transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Invio in corso…
                  </>
                ) : (
                  <>
                    Invia la richiesta <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {phase === "success" && (
          <div
            className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm"
            data-testid="orizzonti-call-success"
          >
            <div className="flex items-center gap-2 text-[#B40101] font-bold uppercase tracking-widest text-xs">
              <CheckCircle2 size={18} /> Richiesta inviata
            </div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold">
              Richiesta inviata
            </h1>
            <p className="mt-4 text-[#666] leading-relaxed">
              {paName ? `${paName} ` : "Il tuo Property Advisor "}
              ha ricevuto la richiesta insieme alle analisi completate. Sarai contattato secondo la modalità e la fascia oraria indicate.
            </p>
            <Link
              to={backHref}
              data-testid="orizzonti-call-success-back"
              className="mt-8 inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8E0101] text-white font-semibold px-5 py-3 rounded-lg transition-colors"
            >
              Torna al mio percorso <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
